import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './ParetoChart.module.css';
import GoToDashboardButton from '../components/GoToDashboardButton';
import SourceFileSummary from './SourceFileSummary';
import { fetchAteSwVersions, fetchParetoData, fetchSerialNumbers, fetchTestspecs } from './paretoChart/paretoApi';
import {
    buildParetoChartData,
    buildParetoOptions,
    filterParetoRows,
    getDatePreset,
    getParetoMetrics,
} from './paretoChart/paretoData';
import { exportParetoChartAsPNG, exportParetoTableAsCSV, exportParetoTableAsPDF } from './paretoChart/paretoExport';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, annotationPlugin);
Modal.setAppElement('#root');

const emptyFilters = {
    startDate: null,
    endDate: null,
    ateSwVersion: '',
    selectedTestspec: '',
    selectedSerialNumbers: [],
    selectedFamilies: [],
};

const ParetoChart = () => {
    const [paretoData, setParetoData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showTable, setShowTable] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [ateSwVersion, setAteSwVersion] = useState('');
    const [ateSwVersions, setAteSwVersions] = useState([]);
    const [selectedTestspec, setSelectedTestspec] = useState('');
    const [testspecs, setTestspecs] = useState([]);
    const [serialNumbers, setSerialNumbers] = useState([]);
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
    const [selectedFamilies, setSelectedFamilies] = useState([]);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);
    const [activeDateField, setActiveDateField] = useState(null);
    const [hideZeroFailSteps, setHideZeroFailSteps] = useState(true);
    const chartRef = useRef(null);

    const currentFilters = useMemo(() => ({
        startDate,
        endDate,
        ateSwVersion,
        selectedTestspec,
        selectedSerialNumbers,
        selectedFamilies,
    }), [ateSwVersion, endDate, selectedFamilies, selectedSerialNumbers, selectedTestspec, startDate]);

    const displayedParetoData = useMemo(
        () => filterParetoRows(paretoData, hideZeroFailSteps),
        [hideZeroFailSteps, paretoData]
    );
    const chartData = useMemo(() => buildParetoChartData(displayedParetoData), [displayedParetoData]);
    const chartOptions = useMemo(() => buildParetoOptions(chartData), [chartData]);
    const metrics = useMemo(() => getParetoMetrics(displayedParetoData), [displayedParetoData]);
    const hasData = displayedParetoData.length > 0;
    const chartRenderKey = useMemo(
        () => displayedParetoData.map((row) => `${row.stepNumber}-${row.failCount}-${row.cumulativePercentage}`).join('|'),
        [displayedParetoData]
    );

    const loadParetoData = useCallback(async (filters) => {
        setLoading(true);
        setError(null);
        try {
            setParetoData(await fetchParetoData(filters));
        } catch (fetchError) {
            console.error('Failed to fetch Pareto data:', fetchError);
            setParetoData([]);
            setError('Failed to load Pareto data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const loadMetadata = async () => {
            try {
                const [versions, serials, specs] = await Promise.all([
                    fetchAteSwVersions(),
                    fetchSerialNumbers(),
                    fetchTestspecs(),
                ]);
                setAteSwVersions(versions);
                setSerialNumbers(serials);
                setTestspecs(specs);
            } catch (fetchError) {
                console.error('Failed to fetch Pareto filter options:', fetchError);
                setError('Some filter options could not be loaded.');
            }
        };

        loadMetadata();
        loadParetoData(emptyFilters);
    }, [loadParetoData]);

    const handleApplyFilters = () => {
        loadParetoData(currentFilters);
    };

    const resetFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setAteSwVersion('');
        setSelectedTestspec('');
        setSelectedSerialNumbers([]);
        setSelectedFamilies([]);
        loadParetoData(emptyFilters);
    };

    const selectDate = (date) => {
        if (activeDateField === 'start') setStartDate(date);
        else setEndDate(date);
        setIsDateModalOpen(false);
    };

    return (
        <div className={styles.container}>
            <GoToDashboardButton />

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>Failure priority</p>
                    <h2 className={styles.pageTitle}>Pareto Failure Analysis</h2>
                    <p className={styles.description}>
                        Identify the steps contributing most to failures and track the cumulative 80 percent threshold.
                    </p>
                </div>
                <button className={styles.helpButtonInline} onClick={() => setIsHelpOpen(true)}>Page help</button>
            </header>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <section className={styles.filterPanel}>
                <div className={styles.filterHeader}>
                    <div>
                        <h3>Filters</h3>
                        <p>Focus the Pareto view by date, software version, and serial number.</p>
                    </div>
                    <button onClick={resetFilters} className={styles.resetButton}>Reset</button>
                </div>

                <div className={styles.filterGrid}>
                    <div className={styles.filterField}>
                        <label>Start date</label>
                        <button className={styles.datePickerButton} onClick={() => { setActiveDateField('start'); setIsDateModalOpen(true); }}>
                            {startDate ? startDate.toLocaleDateString() : 'Any start date'}
                        </button>
                    </div>
                    <div className={styles.filterField}>
                        <label>End date</label>
                        <button className={styles.datePickerButton} onClick={() => { setActiveDateField('end'); setIsDateModalOpen(true); }}>
                            {endDate ? endDate.toLocaleDateString() : 'Any end date'}
                        </button>
                    </div>
                    <div className={styles.filterField}>
                        <label>ATE SW version</label>
                        <select value={ateSwVersion} onChange={(event) => setAteSwVersion(event.target.value)} className={styles.dropdown}>
                            <option value="">All versions</option>
                            {ateSwVersions.map((version) => (
                                <option key={version.value} value={version.value}>{version.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={`${styles.filterField} ${styles.serialFilter}`}>
                        <label>Serial numbers</label>
                        <Select
                            isMulti
                            options={serialNumbers}
                            value={selectedSerialNumbers}
                            onChange={(values) => setSelectedSerialNumbers(values || [])}
                            placeholder="All serial numbers"
                            className={styles.multiSelect}
                            classNamePrefix="paretoSelect"
                        />
                    </div>
                    <div className={`${styles.filterField} ${styles.serialFilter}`}>
                        <label>Testspec / code considered</label>
                        <select value={selectedTestspec} onChange={(event) => setSelectedTestspec(event.target.value)} className={styles.dropdown}>
                            <option value="">All testspecs</option>
                            {testspecs.map((testspec) => (
                                <option key={testspec.value} value={testspec.value}>{testspec.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.filterFooter}>
                    <span className={styles.filterHint}>
                        Code considered: {selectedTestspec || 'all testspecs'}; {selectedSerialNumbers.length ? `${selectedSerialNumbers.length} serial number${selectedSerialNumbers.length === 1 ? '' : 's'} selected` : 'all serial numbers'}
                    </span>
                    <button onClick={handleApplyFilters} className={styles.applyButton}>Apply filters</button>
                </div>

                <SourceFileSummary
                    compact
                    startDate={startDate}
                    endDate={endDate}
                    ateSwVersion={ateSwVersion}
                    testspec={selectedTestspec}
                    serialNumbers={selectedSerialNumbers}
                    selectedFamilies={selectedFamilies}
                    onFamiliesChange={setSelectedFamilies}
                    title="Source files used for dashboard charts"
                />
            </section>

            <section className={styles.metricsGrid}>
                {metrics.map((metric) => (
                    <div className={`${styles.metricCard} ${styles[metric.tone]}`} key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                    </div>
                ))}
            </section>

            <section className={styles.chartPanel}>
                <div className={styles.panelHeader}>
                    <div>
                        <h3>Fail count and cumulative impact</h3>
                        <p>Bars show fail count; the line shows cumulative fail percentage.</p>
                    </div>
                    <div className={styles.panelActions}>
                        <button className={styles.secondaryButton} onClick={() => setHideZeroFailSteps((prev) => !prev)}>
                            {hideZeroFailSteps ? 'Show all steps' : 'Hide 0-fail steps'}
                        </button>
                        <button className={styles.secondaryButton} onClick={() => setIsModalOpen(true)} disabled={!hasData}>Expand chart</button>
                    </div>
                </div>

                <div className={styles.chartContainer} ref={chartRef}>
                    {loading ? (
                        <div className={styles.loadingScreen}><div className={styles.spinner}></div><p>Loading Pareto data...</p></div>
                    ) : hasData ? (
                        <div className={styles.chartCanvas}>
                            <Bar key={chartRenderKey} data={chartData} options={chartOptions} redraw />
                        </div>
                    ) : (
                        <div className={styles.emptyState}>No Pareto data found for these filters.</div>
                    )}
                </div>
            </section>

            <section className={styles.tablePanel}>
                <div className={styles.panelHeader}>
                    <div>
                        <h3>Pareto table</h3>
                        <p>Rows below the 80 percent cumulative threshold are highlighted.</p>
                    </div>
                    <div className={styles.exportButtons}>
                        <button onClick={() => setShowTable((visible) => !visible)} className={styles.secondaryButton}>
                            {showTable ? 'Hide table' : 'Show table'}
                        </button>
                        <button onClick={() => exportParetoChartAsPNG(chartRef)} className={styles.secondaryButton} disabled={!hasData}>Chart PNG</button>
                        <button onClick={() => exportParetoTableAsCSV(displayedParetoData)} className={styles.secondaryButton} disabled={!hasData}>CSV</button>
                        <button onClick={() => exportParetoTableAsPDF(displayedParetoData)} className={styles.secondaryButton} disabled={!hasData}>PDF</button>
                    </div>
                </div>

                {showTable && (
                    <div className={styles.tableWrap}>
                        <table className={styles.styledTable}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Step number</th>
                                    <th>Step name</th>
                                    <th>Total count</th>
                                    <th>Fail count</th>
                                    <th>Fail percentage</th>
                                    <th>Cumulative percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedParetoData.map((data, index) => (
                                    <tr key={data.stepNumber} className={Number(data.cumulativePercentage) < 80 ? styles.redRow : ''}>
                                        <td>{index + 1}</td>
                                        <td>{data.stepNumber}</td>
                                        <td>{data.stepName || 'No Name'}</td>
                                        <td>{data.totCount || 0}</td>
                                        <td>{data.failCount || 0}</td>
                                        <td>{Number(data.failPercentage || 0).toFixed(2)}%</td>
                                        <td>{Number(data.cumulativePercentage || 0).toFixed(2)}%</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <Modal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} className={styles.modalContent} overlayClassName={styles.modalOverlay} contentLabel="Expanded Pareto Chart">
                <button onClick={() => setIsModalOpen(false)} className={styles.closeButton}>Close</button>
                <div className={styles.expandedChartContainer}>
                    <div className={styles.chartScroller}>
                        <div className={styles.expandedChartCanvas} style={{ minWidth: `${chartData.minimumChartWidth}px` }}>
                            <Bar key={`${chartRenderKey}-modal`} data={chartData} options={chartOptions} redraw />
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={isHelpOpen} onRequestClose={() => setIsHelpOpen(false)} className={styles.helpModalContent} overlayClassName={styles.helpModalOverlay} contentLabel="Help Information">
                <button onClick={() => setIsHelpOpen(false)} className={styles.closeHelpButton}>Close</button>
                <div className={styles.helpText}>
                    <h2>About this page</h2>
                    <p>This page ranks failed steps by impact. The bar chart shows fail counts and the cumulative line shows how quickly the biggest contributors reach the 80 percent threshold.</p>
                    <p>Filters come from uploaded test data: dates, ATE software versions, and serial numbers.</p>
                </div>
            </Modal>

            <Modal isOpen={isDateModalOpen} onRequestClose={() => setIsDateModalOpen(false)} className={styles.dateModalContent} overlayClassName={styles.modalOverlay} contentLabel="Select Date">
                <h2>Select {activeDateField === 'start' ? 'start' : 'end'} date</h2>
                <div className={styles.quickSelectContainer}>
                    <button onClick={() => selectDate(new Date())} className={styles.quickSelectButton}>Today</button>
                    <button onClick={() => selectDate(getDatePreset('yesterday'))} className={styles.quickSelectButton}>Yesterday</button>
                    <button onClick={() => selectDate(getDatePreset('sevenDaysAgo'))} className={styles.quickSelectButton}>7 days ago</button>
                    <button onClick={() => selectDate(getDatePreset('thirtyDaysAgo'))} className={styles.quickSelectButton}>30 days ago</button>
                    <button onClick={() => selectDate(null)} className={`${styles.quickSelectButton} ${styles.clearButton}`}>Clear</button>
                </div>
                <DatePicker
                    selected={activeDateField === 'start' ? startDate : endDate}
                    onChange={selectDate}
                    inline
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                />
                <div className={styles.modalActions}>
                    <button onClick={() => setIsDateModalOpen(false)} className={styles.closeModalButton}>Cancel</button>
                </div>
            </Modal>
        </div>
    );
};

export default ParetoChart;
